// syncService.js - Gestion de la synchronisation bidirectionnelle Rapteck API <-> PWA
// ✅ Migré vers le nouveau backend Rapteck
import inspectionStorage from './inspectionStorage';
import apiService from './apiService';

// Clés/champs base64 connus à NE PAS envoyer au backend (déjà uploadés séparément
// via apiService.uploadFile, on garde seulement les URL côté serveur).
// Évite l'erreur HTTP 413 "request entity too large".
const BASE64_FIELD_NAMES = new Set([
  'blob_base64',
  'photo_base64',
  'image_base64',
  'base64',
  'pdf_base64',
  'file_base64',
]);

const isLikelyDataUrl = (val) =>
  typeof val === 'string' && val.length > 1024 && val.startsWith('data:');

/**
 * Strip récursivement tous les blobs base64 d'un objet/tableau.
 * - retire les clés listées dans BASE64_FIELD_NAMES (valeurs d'objet)
 * - retire toute string `data:...` lourde (> 1 Ko), y compris dans les ARRAYS
 *   (ex: observation.photos = ["data:image/jpeg;base64,..."])
 * - les valeurs "object photo" ({ url, file_url, ... }) sont conservées si elles
 *   contiennent une URL valide ; les data: inline sont retirées.
 *
 * Retourne une nouvelle structure (ne mute pas l'entrée).
 */
const stripBase64Recursive = (value) => {
  if (Array.isArray(value)) {
    // Filtre AUSSI les data: directement dans les tableaux (cas observation.photos)
    return value
      .filter((v) => !isLikelyDataUrl(v))
      .map(stripBase64Recursive);
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (BASE64_FIELD_NAMES.has(k)) continue;
      if (isLikelyDataUrl(v)) continue;
      out[k] = stripBase64Recursive(v);
    }
    return out;
  }
  return value;
};

/**
 * Sanitize l'ensemble des steps avant l'envoi au backend.
 * - supprime la clé `pending_photos` (gérée séparément en local)
 * - supprime `etape_3_photos_machine_levage.pending_photos` (ancienne structure)
 * - strip récursivement tous les blobs base64
 * Renvoie aussi les tailles avant/après pour debug.
 */
export const sanitizeStepsForSync = (steps) => {
  const before = (() => {
    try { return JSON.stringify(steps).length; } catch (_) { return 0; }
  })();

  const cleaned = { ...steps };
  delete cleaned.pending_photos;
  if (cleaned.etape_3_photos_machine_levage) {
    const { pending_photos: _ignored, ...rest } = cleaned.etape_3_photos_machine_levage;
    cleaned.etape_3_photos_machine_levage = rest;
  }

  const stripped = stripBase64Recursive(cleaned);

  const after = (() => {
    try { return JSON.stringify(stripped).length; } catch (_) { return 0; }
  })();

  return { payload: stripped, before, after };
};

/**
 * Convertit une dataURL base64 en File pour upload S3.
 * Retourne null si la conversion échoue.
 */
const dataUrlToFileForUpload = (dataUrl, filename) => {
  try {
    const parts = String(dataUrl).split(',');
    const header = parts[0] || '';
    const b64 = parts[1] || '';
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch (err) {
    console.error('[syncService] dataUrlToFileForUpload error:', err);
    return null;
  }
};

/**
 * Parcourt récursivement la structure et remplace chaque dataURL base64
 * rencontrée DANS UN ARRAY par son URL S3 (via apiService.uploadFile).
 * Retourne la nouvelle structure + statistiques d'upload.
 *
 * En cas d'échec : la dataURL reste en place (sera retirée par stripBase64Recursive
 * au moment du sync, mais conservée localement pour retry à la prochaine sync).
 */
const uploadDataUrlsRecursive = async (value, ctx) => {
  if (Array.isArray(value)) {
    const out = [];
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (isLikelyDataUrl(item)) {
        const filename = `photo_observation_${Date.now()}_${ctx.counter++}.jpg`;
        const file = dataUrlToFileForUpload(item, filename);
        if (file) {
          try {
            const result = await apiService.uploadFile(file, 'photo_observation');
            if (result?.success && result?.file_url) {
              out.push(result.file_url);
              ctx.uploaded++;
              continue;
            }
            ctx.failed++;
            console.warn(`[syncService] Upload échoué pour ${filename}:`, result?.error);
          } catch (err) {
            ctx.failed++;
            console.warn(`[syncService] Upload erreur pour ${filename}:`, err?.message || err);
          }
        }
        // Échec : on garde la dataURL en local (sera strippée au sync, retentée plus tard)
        out.push(item);
      } else {
        out.push(await uploadDataUrlsRecursive(item, ctx));
      }
    }
    return out;
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = await uploadDataUrlsRecursive(v, ctx);
    }
    return out;
  }
  return value;
};

/**
 * Avant chaque sync mission : upload en S3 toutes les photos b64 restantes
 * dans les étapes (observations, armoires, etc.) et persiste les URL en local.
 * Retourne les allSteps mis à jour + stats.
 */
export const uploadPendingObservationPhotos = async (missionId, allSteps) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { allSteps, uploaded: 0, failed: 0 };
  }
  const ctx = { uploaded: 0, failed: 0, counter: 0 };
  const updatedSteps = {};
  for (const [stepKey, stepValue] of Object.entries(allSteps || {})) {
    // On évite de toucher aux clés admin/meta
    if (stepKey === 'pending_photos' || stepKey === 'meta' || stepKey === 'sync_status' || stepKey.endsWith('_last_sync') || stepKey.endsWith('_last_local_update')) {
      updatedSteps[stepKey] = stepValue;
      continue;
    }
    const before = ctx.uploaded;
    updatedSteps[stepKey] = await uploadDataUrlsRecursive(stepValue, ctx);
    if (ctx.uploaded > before) {
      // Persiste localement la version "URLs" pour ne pas réuploader la prochaine fois
      try {
        await inspectionStorage.set(`mission_${missionId}_${stepKey}`, updatedSteps[stepKey]);
      } catch (err) {
        console.warn(`[syncService] Persistance locale ${stepKey} échouée:`, err?.message || err);
      }
    }
  }
  if (ctx.uploaded > 0 || ctx.failed > 0) {
    console.log(`📤 Upload photos pré-sync : ${ctx.uploaded} uploadées, ${ctx.failed} échouées`);
  }
  return { allSteps: updatedSteps, uploaded: ctx.uploaded, failed: ctx.failed };
};

class SyncService {
  constructor() {
    this.syncQueue = [];
    this.isSyncing = false;
  }

  dataUrlToFile(dataUrl, filename) {
    try {
      const parts = dataUrl.split(',');
      const header = parts[0] || '';
      const base64 = parts[1] || '';
      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new File([bytes], filename, { type: mime });
    } catch (error) {
      console.error('❌ Erreur conversion dataUrl:', error);
      return null;
    }
  }

  async uploadPendingPhotos(missionId, allSteps) {
    const stepKey = 'etape_3_photos_machine_levage';
    const step = allSteps?.[stepKey];
    if (!step || !Array.isArray(step.pending_photos) || step.pending_photos.length === 0) {
      return { updated: false, allSteps };
    }
    if (!navigator.onLine) {
      return { updated: false, allSteps };
    }

    const remaining = [];
    const uploadedUrls = [];
    for (let i = 0; i < step.pending_photos.length; i++) {
      const item = step.pending_photos[i];
      const dataUrl = typeof item === 'string' ? item : item?.dataUrl;
      const name = typeof item === 'object' && item?.name ? item.name : `photo-offline-${Date.now()}-${i}.jpg`;
      if (!dataUrl) {
        continue;
      }
      const file = this.dataUrlToFile(dataUrl, name);
      if (!file) {
        remaining.push(item);
        continue;
      }
      const result = await apiService.uploadFile(file, 'photo_levage');
      if (result?.success && result?.file_url) {
        uploadedUrls.push(result.file_url);
      } else {
        remaining.push(item);
      }
    }

    if (uploadedUrls.length > 0 || remaining.length !== step.pending_photos.length) {
      const nextStep = {
        ...step,
        photos: [...(step.photos || []), ...uploadedUrls],
        pending_photos: remaining
      };
      const storageKey = `mission_${missionId}_${stepKey}`;
      await inspectionStorage.set(storageKey, nextStep);
      allSteps[stepKey] = nextStep;
      return { updated: true, allSteps };
    }

    return { updated: false, allSteps };
  }
  
  /**
   * 🔥 Télécharger une mission depuis Rapteck API et la stocker dans IndexedDB
   */
  async downloadMission(missionId) {
    try {
      console.log(`📥 Téléchargement mission ${missionId}...`);

      const isDirty = await inspectionStorage.isDirty(missionId);
      if (isDirty) {
        console.warn('⚠️ Données locales non synchronisées, tentative d\'upload avant téléchargement...');
        const uploadResult = await this.uploadMission(missionId);
        if (!uploadResult.success) {
          console.warn('⚠️ Upload impossible, téléchargement des données serveur quand même...');
          // Ne pas bloquer : on tente quand même le téléchargement pour que l'utilisateur puisse continuer
        }
      }
      
      // 1. Récupérer les données depuis Rapteck API
      const response = await apiService.getMissionDetails(missionId);
      
      if (!response.success || !response.data) {
        console.error('❌ Échec récupération mission:', response.error || response.message);
        const errorMsg = response.error || response.message || 'Mission non trouvée';
        if (isDirty) {
          return { success: false, error: `Impossible de récupérer les données serveur. ${errorMsg}. Vous pouvez continuer avec les données locales.` };
        }
        return { success: false, error: errorMsg };
      }
      
      const serverMission = response.data;
      console.log('📦 Données reçues de Rapteck:', serverMission.type_mission);
      
      // 2. Utiliser la méthode existante de inspectionStorage pour sync
      const applied = await inspectionStorage.syncFromServer(missionId, serverMission);
      
      if (!applied) {
        return { success: false, error: 'Synchronisation locale ignorée' };
      }
      
      console.log(`✅ Mission ${missionId} téléchargée et stockée via syncFromServer`);
      return { success: true, mission: serverMission };
      
    } catch (error) {
      console.error('❌ Erreur downloadMission:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Synchroniser une étape vers Base44
   */
  async syncStep(missionId, stepKey, stepData) {
    if (!navigator.onLine) {
      console.log('📴 Hors ligne - sync différée pour', stepKey);
      return false;
    }

    // Ne jamais pousser le store local des photos en attente (base64 lourd)
    if (stepKey === 'pending_photos') {
      console.log('⏭️ Sync ignoree pour pending_photos (gere localement)');
      return true;
    }

    try {
      const { payload: cleanedSteps } = sanitizeStepsForSync({ [stepKey]: stepData });
      const result = await apiService.syncMissionData(missionId, cleanedSteps);

      if (result.success) {
        console.log(`✅ Sync réussie pour ${stepKey}`);
        return true;
      } else {
        console.warn(`⚠️ Sync échouée pour ${stepKey}`);
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur sync:', error);
      return false;
    }
  }

  /**
   * 🔥 Synchroniser toutes les missions
   */
  async syncAllMissions(missions = []) {
    console.log('🔄 Synchronisation de toutes les missions...');
    let successCount = 0;
    let errorCount = 0;

    for (const mission of missions) {
      try {
        const result = await this.fullSync(mission.id);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`❌ Erreur sync mission ${mission.id}:`, error);
        errorCount++;
      }
    }

    console.log(`✅ Sync terminée: ${successCount} réussies, ${errorCount} erreurs`);
    return { success: errorCount === 0, successCount, errorCount };
  }
  
  /**
   * 🔥 Upload les données locales vers Base44
   *
   * Pipeline (dans l'ordre, chaque étape pouvant échouer sans bloquer la suivante) :
   *   1. uploadPendingPhotos   : photos levage (pending_photos store dédié)
   *   2. uploadPendingObservationPhotos : photos b64 inline dans les obs (→ S3)
   *   3. sanitizeStepsForSync  : strip des b64 restantes (filet de sécurité)
   *   4. apiService.syncMissionData : envoi du payload propre au backend
   */
  async uploadMission(missionId) {
    try {
      console.log(`📤 Upload mission ${missionId}...`);
      
      // Récupérer toutes les étapes depuis IndexedDB
      let allSteps = await inspectionStorage.getAllSteps(missionId);
      
      if (!allSteps || Object.keys(allSteps).length === 0) {
        console.log('ℹ️ Aucune donnée locale à uploader');
        return { success: true, message: 'Rien à uploader' };
      }
      
      // 1) Upload des photos offline levage (store pending_photos dédié)
      const pendingResult = await this.uploadPendingPhotos(missionId, allSteps);
      if (pendingResult?.updated) {
        allSteps = pendingResult.allSteps;
      }

      // 2) Upload des photos b64 inline dans les observations (étapes électricité, etc.)
      //    → remplace les data:... par des URL S3 et persiste localement
      try {
        const obsUpload = await uploadPendingObservationPhotos(missionId, allSteps);
        if (obsUpload?.uploaded > 0) {
          allSteps = obsUpload.allSteps;
        }
      } catch (err) {
        console.warn('⚠️ uploadPendingObservationPhotos a échoué (non bloquant):', err?.message || err);
      }

      // 3) Nettoyer les champs offline + strip base64 avant envoi (filet de sécurité)
      const { payload: payloadSteps, before, after } = sanitizeStepsForSync(allSteps);
      const beforeMB = (before / 1024 / 1024).toFixed(2);
      const afterMB = (after / 1024 / 1024).toFixed(2);
      console.log(`📦 Sync payload: ${beforeMB} MB → ${afterMB} MB (apres strip base64)`);

      const payloadWithPending = await attachPendingPhotosToSyncPayload(missionId, payloadSteps);

      // 4) Envoyer vers Rapteck API
      const result = await apiService.syncMissionData(missionId, payloadWithPending);
      
      if (result.success) {
        await inspectionStorage.set(`mission_${missionId}_last_sync`, new Date().toISOString());
        console.log(`✅ Mission ${missionId} uploadée`);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Erreur uploadMission:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * 🔥 Synchronisation complète bidirectionnelle
   */
  async fullSync(missionId) {
    try {
      console.log(`🔄 Sync complète mission ${missionId}...`);
      
      // 1. D'abord télécharger les données du serveur
      const downloadResult = await this.downloadMission(missionId);
      
      if (!downloadResult.success) {
        console.warn('⚠️ Download échoué, tentative upload quand même');
      }
      
      // 2. Ensuite uploader les modifications locales
      const uploadResult = await this.uploadMission(missionId);
      
      return {
        success: downloadResult.success || uploadResult.success,
        download: downloadResult,
        upload: uploadResult
      };
      
    } catch (error) {
      console.error('❌ Erreur fullSync:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Télécharger les templates d'observations levage depuis Base44
   */
  async downloadTemplatesLevage() {
    try {
      console.log('📥 Téléchargement templates observations levage...');
      
      const response = await apiService.getTemplatesLevage();
      console.log('📦 Réponse API templates:', response);
      
      if (!response.success || !response.templates) {
        console.error('❌ Échec récupération templates levage:', response.error);
        return { success: false, error: response.error || 'Templates non trouvés' };
      }
      
      // Stocker les templates dans IndexedDB
      await inspectionStorage.set('templates_observations_levage', response.templates);
      await inspectionStorage.set('templates_levage_updated_at', new Date().toISOString());
      
      console.log(`✅ ${response.templates.length} templates levage téléchargés`);
      return { success: true, count: response.templates.length };
      
    } catch (error) {
      console.error('❌ Erreur downloadTemplatesLevage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Récupérer les templates levage depuis le stockage local
   */
  async getLocalTemplatesLevage(familleCode, numeroPoint) {
    try {
      const templates = await inspectionStorage.get('templates_observations_levage');
      
      if (!templates) {
        return [];
      }
      
      // Filtrer par famille et numéro de point
      return templates.filter(t => 
        t.actif !== false &&
        t.famille_code === familleCode &&
        t.numero_point === numeroPoint
      );
      
    } catch (error) {
      console.error('❌ Erreur getLocalTemplatesLevage:', error);
      return [];
    }
  }

  /**
   * 🔥 Télécharger les grilles de points de contrôle levage depuis Base44
   */
  async downloadGrillesLevage() {
    try {
      console.log('📥 Téléchargement grilles points de contrôle levage...');
      
      const response = await apiService.getGrillesPointsControleLevage();
      console.log('📦 Réponse API grilles:', response);
      
      if (!response || !response.success || !response.grilles) {
        const errorMsg = response?.error || 'Grilles non trouvées';
        console.error('❌ Échec récupération grilles levage:', errorMsg);
        return { success: false, error: errorMsg };
      }
      
      // Stocker les grilles dans IndexedDB
      console.log('💾 Stockage des grilles:', Object.keys(response.grilles));
      await inspectionStorage.set('grilles_points_controle_levage', response.grilles);
      await inspectionStorage.set('grilles_levage_updated_at', new Date().toISOString());
      
      console.log(`✅ ${response.count} grilles levage téléchargées et stockées`);
      return { success: true, count: response.count };
      
    } catch (error) {
      console.error('❌ Erreur downloadGrillesLevage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Récupérer une grille de points de contrôle depuis le stockage local
   */
  async getLocalGrilleLevage(codeAnnexe) {
    try {
      const grilles = await inspectionStorage.get('grilles_points_controle_levage');

      if (!grilles) {
        console.warn('⚠️ Aucune grille en cache local pour', codeAnnexe);
        return null;
      }

      console.log('📦 Grilles disponibles:', Object.keys(grilles));

      // Retourner la grille correspondante
      if (grilles[codeAnnexe]) {
        console.log('✅ Grille trouvée pour', codeAnnexe);
        return grilles[codeAnnexe];
      }

      console.warn('⚠️ Grille non trouvée pour', codeAnnexe, '- pas de fallback');
      return null;

    } catch (error) {
      console.error('❌ Erreur getLocalGrilleLevage:', error);
      return null;
    }
  }

  /**
   * 🔥 Télécharger le référentiel UTE C15-103 (locaux) depuis Base44
   */
  async downloadReferentielLocaux() {
    try {
      console.log('📥 Téléchargement référentiel UTE C15-103...');
      
      const response = await apiService.getReferentielLocaux();
      
      if (!response || !response.success || !response.referentiels) {
        const errorMsg = response?.error || 'Référentiel non trouvé';
        console.error('❌ Échec récupération référentiel locaux:', errorMsg);
        return { success: false, error: errorMsg };
      }
      
      // Stocker le référentiel dans IndexedDB
      await inspectionStorage.set('referentiel_locaux', response.referentiels);
      await inspectionStorage.set('referentiel_locaux_updated_at', new Date().toISOString());
      
      console.log(`✅ ${response.referentiels.length} locaux UTE C15-103 téléchargés et stockés`);
      return { success: true, count: response.referentiels.length };
      
    } catch (error) {
      console.error('❌ Erreur downloadReferentielLocaux:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Télécharger la bibliothèque d'appareils de référence
   */
  async downloadAppareilsReference() {
    try {
      console.log('📥 Téléchargement appareils de référence...');
      
      const response = await apiService.getAppareilsReference();
      
      if (!response || !response.success) {
        const errorMsg = response?.error || 'Appareils non trouvés';
        console.error('❌ Échec récupération appareils:', errorMsg);
        return { success: false, error: errorMsg };
      }
      
      await inspectionStorage.set('appareils_reference', response.appareils);
      await inspectionStorage.set('appareils_reference_updated_at', new Date().toISOString());
      
      console.log(`✅ ${response.appareils.length} appareils de référence téléchargés`);
      return { success: true, count: response.appareils.length };
      
    } catch (error) {
      console.error('❌ Erreur downloadAppareilsReference:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🔥 Récupérer les appareils de référence depuis le cache local
   */
  async getLocalAppareilsReference() {
    try {
      const appareils = await inspectionStorage.get('appareils_reference');
      return appareils || [];
    } catch (error) {
      console.error('❌ Erreur getLocalAppareilsReference:', error);
      return [];
    }
  }
}

const syncService = new SyncService();

/**
 * Prépare une étape avant envoi au backend : upload S3 des photos offline
 * (levage pending_photos + base64 inline) puis retourne les données prêtes à sync.
 */
export const prepareStepForSync = async (missionId, stepKey, stepData) => {
  if (stepKey === 'pending_photos') {
    return { skip: true };
  }

  let dataToSync = stepData;

  if (stepKey === 'etape_3_photos_machine_levage') {
    const wrapper = { [stepKey]: stepData };
    const pendingResult = await syncService.uploadPendingPhotos(missionId, wrapper);
    if (pendingResult?.updated) {
      dataToSync = pendingResult.allSteps[stepKey];
    }
  }

  const { allSteps: withUrls } = await uploadPendingObservationPhotos(missionId, {
    [stepKey]: dataToSync,
  });
  return { stepData: withUrls[stepKey] ?? dataToSync };
};

/** Ajoute etape_11_pending_photos (URLs + contexte, sans base64) au payload sync. */
export const attachPendingPhotosToSyncPayload = async (missionId, payload) => {
  try {
    const pendingPhotosService = (await import('./pendingPhotosService')).default;
    const etape_11_pending_photos = await pendingPhotosService.buildSyncPayload(missionId);
    return { ...payload, etape_11_pending_photos };
  } catch (err) {
    console.warn('[syncService] attachPendingPhotosToSyncPayload:', err?.message || err);
    return payload;
  }
};

export default syncService;